import type { MouseEvent } from 'react';
import Image from 'next/image';

import LinkButton from '_components/UI/LinkButton';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';

import type { LearnStripCard } from '../types';

import styles from './StoryCard.module.scss';

export interface StoryCardProps extends LearnStripCard {
  /** Skips lazy loading for the cards that are on screen on arrival. */
  priority?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * A single Stories Card in the Learn Strip.
 *
 * The CTA is the card's only interactive element and its hit area is stretched
 * over the whole tile, rather than the tile being one big anchor. That keeps
 * the paused "View Products" control (ED-356) a valid sibling of the link
 * instead of a button nested inside one.
 *
 * The heading is an `h3` because the strip titles itself with an `h2`.
 *
 * CTA urls are authored by hand in Prismic, so a relative one is localized here
 * rather than trusted as written — a locale-less internal path would 404 under
 * the site's `/[locale]` routing.
 */
const StoryCard = ({
  heading,
  subheading,
  cta,
  image,
  priority = false,
  onClick,
}: StoryCardProps) => {
  const locale = useLocale();

  return (
    <article className={styles.card}>
      <div className={styles.imageWrapper}>
        <Image
          src={image.url}
          alt={image.alt}
          fill
          sizes="(min-width: 988px) 600px, 340px"
          priority={priority}
          className={styles.image}
        />
      </div>
      {/* Gradient scrim keeps the white copy above the WCAG AA ratio whatever
          editorial photo sits behind it. */}
      <span className={styles.scrim} aria-hidden="true" />
      <div className={styles.content}>
        <div className={styles.text}>
          {subheading && <p className={styles.subheading}>{subheading}</p>}
          <h3 className={styles.heading}>{heading}</h3>
        </div>
        <LinkButton
          href={localizeHref(cta.url, locale)}
          className={styles.cta}
          onClick={onClick}
        >
          {cta.label}
          {/* Every card in a strip tends to share one CTA label, so the heading
              rides along in the accessible name to keep the links distinct when
              they are listed out of context. */}
          <span className={styles.srOnly}>{` ${heading}`}</span>
        </LinkButton>
      </div>
    </article>
  );
};

export default StoryCard;
